import { useEffect, useState } from 'react';
import { HelpCircle } from 'lucide-react';
import { Modal } from '@/components/ui/modal';
import { EditorField, EdSelect, EdInput } from './editor-field';
import { ToggleSwitch } from './toggle-switch';
import type { ConditionEngineRules, DraftAction } from './editor-types';

function TrailingStopInfo() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="cursor-pointer">
        <HelpCircle size={11} className="text-ink-4 hover:text-orange transition-colors" />
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title="Trailing Stop">
        <div className="space-y-4 text-xs text-ink-2 leading-relaxed">
          <p>A trailing stop follows the price in your favor and closes the position when the price reverses by a specified distance. It locks in profits while letting winners run.</p>

          <div className="h-px bg-border" />

          <div>
            <h3 className="text-[11px] font-bold text-ink-1 mb-1">Type: ATR Multiple</h3>
            <p>Stop distance is based on the Average True Range indicator, adapting to current market volatility.</p>
            <div className="bg-surface rounded-lg px-3 py-2 mt-1.5">
              <p className="text-[10px] text-ink-3">ATR(14) = 15 pips, Value = <strong className="text-ink-2">2.0</strong> → stop trails at <strong className="text-ink-1">30 pips</strong> behind the best price.</p>
            </div>
            <p className="text-ink-3 mt-1">Best for: most strategies. Automatically widens in volatile markets and tightens in calm markets.</p>
          </div>

          <div className="h-px bg-border" />

          <div>
            <h3 className="text-[11px] font-bold text-ink-1 mb-1">Type: Percent</h3>
            <p>Stop distance is a percentage of the current price.</p>
            <div className="bg-surface rounded-lg px-3 py-2 mt-1.5">
              <p className="text-[10px] text-ink-3">Price = $2,000, Value = <strong className="text-ink-2">0.5</strong> → stop trails at <strong className="text-ink-1">$10</strong> ($2,000 × 0.5%) behind the best price.</p>
            </div>
          </div>

          <div className="h-px bg-border" />

          <div>
            <h3 className="text-[11px] font-bold text-ink-1 mb-1">Type: Fixed Distance</h3>
            <p>Stop distance is a fixed number of pips/points, regardless of price or volatility.</p>
            <div className="bg-surface rounded-lg px-3 py-2 mt-1.5">
              <p className="text-[10px] text-ink-3">Value = <strong className="text-ink-2">50</strong> → stop always trails exactly <strong className="text-ink-1">50 pips</strong> behind the best price.</p>
            </div>
          </div>

          <div className="h-px bg-border" />

          <div>
            <h3 className="text-[11px] font-bold text-orange mb-1.5">How it works</h3>
            <div className="bg-surface rounded-lg px-3 py-2.5 font-mono text-[10px] text-ink-3 space-y-0.5">
              <p>BUY at <strong className="text-ink-1">$2,000</strong>, trailing = 30 pips</p>
              <p>Price → $2,010: stop moves to <strong className="text-ink-1">$2,007</strong></p>
              <p>Price → $2,020: stop moves to <strong className="text-ink-1">$2,017</strong></p>
              <p>Price → $2,015: stop stays at <strong className="text-ink-1">$2,017</strong></p>
              <p>Price → $2,017: <strong className="text-red">position closed</strong> (+$17)</p>
            </div>
            <p className="text-ink-3 mt-2">The stop only moves in your favor — never backward. When the price reverses and hits the stop, the position is closed with whatever profit was locked in.</p>
          </div>
        </div>
      </Modal>
    </>
  );
}

function TrailingTypeInfo() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="cursor-pointer">
        <HelpCircle size={11} className="text-ink-4 hover:text-orange transition-colors" />
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title="Trailing Stop Type">
        <div className="space-y-4 text-xs text-ink-2 leading-relaxed">
          <p>How the trailing stop distance is calculated.</p>

          <div className="space-y-1.5">
            <div className="bg-surface rounded-lg px-3 py-2">
              <span className="text-[10px] font-bold text-ink-3">ATR Multiple</span>
              <p className="text-[10px] text-ink-4 mt-0.5">Distance = ATR × value. Adapts to market volatility — widens in choppy markets, tightens in calm ones.</p>
            </div>
            <div className="bg-surface rounded-lg px-3 py-2">
              <span className="text-[10px] font-bold text-ink-3">Percent</span>
              <p className="text-[10px] text-ink-4 mt-0.5">Distance = price × value%. Scales with the instrument's price level.</p>
            </div>
            <div className="bg-surface rounded-lg px-3 py-2">
              <span className="text-[10px] font-bold text-ink-3">Fixed Distance</span>
              <p className="text-[10px] text-ink-4 mt-0.5">Distance = fixed pips/points. Same distance regardless of price or volatility.</p>
            </div>
          </div>

          <p className="text-ink-4"><strong className="text-ink-3">ATR Multiple</strong> is recommended for most strategies as it automatically adjusts to changing market conditions.</p>
        </div>
      </Modal>
    </>
  );
}

function BasketModeInfo() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="cursor-pointer">
        <HelpCircle size={11} className="text-ink-4 hover:text-orange transition-colors" />
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title="Trailing Stop Mode">
        <div className="space-y-4 text-xs text-ink-2 leading-relaxed">
          <div>
            <h3 className="text-[11px] font-bold text-ink-1 mb-1">Per Position</h3>
            <p>Each position gets its own trailing stop. When the price reverses, that individual position is closed. Good for trend-following strategies.</p>
          </div>
          <div className="h-px bg-border" />
          <div>
            <h3 className="text-[11px] font-bold text-ink-1 mb-1">Basket (Grid)</h3>
            <p>Trails the <strong className="text-ink-1">total floating PnL</strong> across all positions. When the exit rule fires (e.g. basket PnL hits $5), instead of closing immediately, the trailing stop activates and tracks the peak PnL. If the PnL pulls back by the trailing amount, all positions close together.</p>
            <div className="bg-surface rounded-lg px-3 py-2.5 font-mono text-[10px] text-ink-3 space-y-0.5 mt-2">
              <p>Basket PnL hits <strong className="text-ink-1">$5</strong> target → trailing activates</p>
              <p>PnL rises to <strong className="text-ink-1">$8</strong> → new peak tracked</p>
              <p>PnL drops to <strong className="text-ink-1">$6</strong> → pullback = $2</p>
              <p>If trailing = $2: <strong className="text-red">CLOSE ALL</strong> (locked in $6)</p>
            </div>
            <p className="text-ink-3 mt-2">Best for grid and martingale strategies where you want to let profits overshoot the target.</p>
          </div>
        </div>
      </Modal>
    </>
  );
}

function TrailingValueInfo({ basketMode }: { basketMode?: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="cursor-pointer">
        <HelpCircle size={11} className="text-ink-4 hover:text-orange transition-colors" />
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title="Trailing Stop Value">
        <div className="space-y-4 text-xs text-ink-2 leading-relaxed">
          <p>The trailing distance amount. Its meaning depends on the type and mode selected:</p>

          {basketMode ? (
            <div className="space-y-1.5">
              <div className="bg-surface rounded-lg px-3 py-2">
                <span className="text-[10px] font-bold text-ink-3">If Fixed Distance</span>
                <p className="text-[10px] text-ink-4 mt-0.5"><strong className="text-ink-3">2</strong> = close all if basket PnL drops <strong className="text-ink-3">$2</strong> from its peak.</p>
              </div>
              <div className="bg-surface rounded-lg px-3 py-2">
                <span className="text-[10px] font-bold text-ink-3">If Percent</span>
                <p className="text-[10px] text-ink-4 mt-0.5"><strong className="text-ink-3">30</strong> = close all if basket PnL drops <strong className="text-ink-3">30%</strong> from its peak. Peak $10 → closes at $7.</p>
              </div>
              <div className="bg-surface rounded-lg px-3 py-2">
                <span className="text-[10px] font-bold text-ink-3">If ATR Multiple</span>
                <p className="text-[10px] text-ink-4 mt-0.5"><strong className="text-ink-3">2.0</strong> = close all if basket PnL drops by ATR × 2 × total volume × contract size from peak.</p>
              </div>
            </div>
          ) : (
            <div className="space-y-1.5">
              <div className="bg-surface rounded-lg px-3 py-2">
                <span className="text-[10px] font-bold text-ink-3">If ATR Multiple</span>
                <p className="text-[10px] text-ink-4 mt-0.5"><strong className="text-ink-3">2.0</strong> = trail at 2× ATR. If ATR is 15 pips, stop trails 30 pips behind.</p>
              </div>
              <div className="bg-surface rounded-lg px-3 py-2">
                <span className="text-[10px] font-bold text-ink-3">If Percent</span>
                <p className="text-[10px] text-ink-4 mt-0.5"><strong className="text-ink-3">0.5</strong> = trail at 0.5% of price. At $2,000, stop trails $10 behind.</p>
              </div>
              <div className="bg-surface rounded-lg px-3 py-2">
                <span className="text-[10px] font-bold text-ink-3">If Fixed Distance</span>
                <p className="text-[10px] text-ink-4 mt-0.5"><strong className="text-ink-3">50</strong> = trail exactly 50 pips behind the best price.</p>
              </div>
            </div>
          )}

          <p className="text-ink-4">Smaller values = tighter stop (locks in more profit but may exit too early). Larger values = looser stop (more room for the trade to breathe).</p>
        </div>
      </Modal>
    </>
  );
}

function AtrPeriodInfo() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="cursor-pointer">
        <HelpCircle size={11} className="text-ink-4 hover:text-orange transition-colors" />
      </button>
      <Modal open={open} onClose={() => setOpen(false)} title="ATR Period">
        <div className="space-y-4 text-xs text-ink-2 leading-relaxed">
          <p>The number of candles used to calculate the Average True Range (ATR). ATR measures market volatility — the average range of price movement per candle.</p>

          <div className="bg-surface rounded-lg px-3 py-2">
            <span className="text-[10px] font-bold text-ink-3">Example</span>
            <p className="text-[10px] text-ink-3 mt-0.5">Period = <strong className="text-ink-2">14</strong> on a 1-hour chart → ATR is the average price range over the last 14 hours of candles.</p>
          </div>

          <div className="h-px bg-border" />

          <div>
            <h3 className="text-[11px] font-bold text-ink-1 mb-1">Common Values</h3>
            <div className="space-y-1.5">
              <div className="bg-surface rounded-lg px-3 py-2 flex items-center justify-between">
                <span className="text-[10px] font-bold text-ink-3">7</span>
                <span className="text-[10px] text-ink-4">Short-term — reacts quickly to volatility changes</span>
              </div>
              <div className="bg-surface rounded-lg px-3 py-2 flex items-center justify-between">
                <span className="text-[10px] font-bold text-ink-3">14</span>
                <span className="text-[10px] text-ink-4">Standard — most widely used (default)</span>
              </div>
              <div className="bg-surface rounded-lg px-3 py-2 flex items-center justify-between">
                <span className="text-[10px] font-bold text-ink-3">21</span>
                <span className="text-[10px] text-ink-4">Longer-term — smoother, less reactive</span>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}

export function TrailingStopSection({
  draft,
  dispatch,
}: {
  draft: ConditionEngineRules;
  dispatch: React.Dispatch<DraftAction>;
}) {
  const ts = draft.exit.trailingStop ?? {
    enabled: false,
    type: 'ATR_MULTIPLE' as const,
    value: 2,
    atrPeriod: 14,
  };

  // Ensure trailingStop exists in draft
  useEffect(() => {
    if (!draft.exit.trailingStop) {
      dispatch({ type: 'SET_PATH', path: 'exit.trailingStop', value: ts });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <EditorField label={
        <span className="flex items-center gap-1">
          Enabled
          <TrailingStopInfo />
        </span>
      }>
        <ToggleSwitch
          value={ts.enabled}
          onChange={(v) => dispatch({ type: 'SET_PATH', path: 'exit.trailingStop.enabled', value: v })}
        />
      </EditorField>

      {ts.enabled && (
        <>
          <EditorField label={
            <span className="flex items-center gap-1">
              Mode
              <BasketModeInfo />
            </span>
          }>
            <EdSelect
              value={ts.basketMode ? 'BASKET' : 'PER_POSITION'}
              onChange={(v) => dispatch({ type: 'SET_PATH', path: 'exit.trailingStop.basketMode', value: v === 'BASKET' })}
              options={[{ value: 'PER_POSITION', label: 'Per Position' }, { value: 'BASKET', label: 'Basket (Grid)' }]}
            />
          </EditorField>
          <EditorField label={
            <span className="flex items-center gap-1">
              Type
              <TrailingTypeInfo />
            </span>
          }>
            <EdSelect
              value={ts.type}
              onChange={(v) => dispatch({ type: 'SET_PATH', path: 'exit.trailingStop.type', value: v })}
              options={['ATR_MULTIPLE', 'PERCENT', 'FIXED_DISTANCE']}
            />
          </EditorField>
          <EditorField label={
            <span className="flex items-center gap-1">
              Value
              <TrailingValueInfo basketMode={!!ts.basketMode} />
            </span>
          }>
            <EdInput
              value={ts.value}
              onChange={(v) => dispatch({ type: 'SET_PATH_NUM', path: 'exit.trailingStop.value', value: v })}
              step="any"
              small
            />
            <span className="text-[10px] text-ink-4">
              {ts.basketMode
                ? (ts.type === 'PERCENT' ? '% of peak' : ts.type === 'ATR_MULTIPLE' ? '× ATR' : '$')
                : (ts.type === 'PERCENT' ? '%' : ts.type === 'ATR_MULTIPLE' ? '× ATR' : 'pips')}
            </span>
          </EditorField>
          {ts.type === 'ATR_MULTIPLE' && (
            <EditorField label={
              <span className="flex items-center gap-1">
                ATR Period
                <AtrPeriodInfo />
              </span>
            }>
              <EdInput
                value={ts.atrPeriod ?? 14}
                onChange={(v) =>
                  dispatch({ type: 'SET_PATH_NUM', path: 'exit.trailingStop.atrPeriod', value: v })
                }
                min="1"
                step="1"
                small
              />
            </EditorField>
          )}
        </>
      )}
    </>
  );
}
